import { useLocalStorage } from '@uidotdev/usehooks';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { IconButton } from '@mui/material';
import { cyan, grey } from '@mui/material/colors';
import { Box } from '@mui/system';

const MAX_PANEL_WIDTH = 512;
const INITIAL_PANEL_WIDTH = 256;
const MIN_PANEL_WIDTH = 70;

type NavigationPanelProps = {
  children: ReactNode;
  isPanelOpen: boolean;
  setIsPanelOpen: (open: boolean) => void;
};

const NavigationPanel: FC<NavigationPanelProps> = ({
  children,
  isPanelOpen,
  setIsPanelOpen,
}) => {
  const [savedNavigationPanelWidth, setSavedNavigationPanelWidth] =
    useLocalStorage('navigation-panel-width', MIN_PANEL_WIDTH);

  const [dragging, setDragging] = useState(false);
  const [isPanelHovered, setIsPanelHovered] = useState(false);
  const [panelWidth, setPanelWidth] = useState(savedNavigationPanelWidth);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleMouseUp = useCallback(() => {
    document.body.style.cursor = 'auto';
    setDragging(false);
    setSavedNavigationPanelWidth(panelWidth);
  }, [panelWidth, setSavedNavigationPanelWidth]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging) return;

      if (e.clientX < MIN_PANEL_WIDTH) return;
      if (e.clientX > MAX_PANEL_WIDTH) return;

      if (!isPanelOpen && e.clientX > INITIAL_PANEL_WIDTH) {
        setIsPanelOpen(true);
      }

      if (isPanelOpen && e.clientX < MIN_PANEL_WIDTH + 10) {
        setIsPanelOpen(false);
      }

      document.body.style.cursor = 'ew-resize';
      setPanelWidth(e.clientX);
    },
    [dragging, isPanelOpen, setIsPanelOpen],
  );

  const handleTogglePanel = (open: boolean) => {
    setIsPanelOpen(open);

    if (!open) {
      setPanelWidth(70);
      setSavedNavigationPanelWidth(70);
    } else {
      setPanelWidth(256);
      setSavedNavigationPanelWidth(256);
    }
  };

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, handleMouseMove, handleMouseUp]);

  useEffect(
    () => setIsPanelOpen(savedNavigationPanelWidth >= INITIAL_PANEL_WIDTH),
    [savedNavigationPanelWidth, setIsPanelOpen],
  );

  return (
    <Box
      onMouseEnter={() => setIsPanelHovered(true)}
      onMouseLeave={() => setIsPanelHovered(false)}
      sx={{
        height: '100vh',
        width: panelWidth,
        backgroundColor: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 'modal',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      {children}

      <Box
        onMouseDown={handleMouseDown}
        sx={{
          'position': 'absolute',
          'top': 0,
          'right': -2,
          'bottom': 0,
          'width': 4,
          'backgroundColor': cyan[100],
          'cursor': 'ew-resize',
          'opacity': dragging ? 1 : 0,
          'transition': 'opacity 0.3s',

          '&:hover': {
            opacity: 1,
          },
        }}
      />

      <Box
        sx={{
          width: 50,
          height: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          top: 10,
          right: '0',
          transform: 'translateX(50%)',
          opacity: isPanelHovered ? 1 : 0,
          transition: 'opacity 0.3s',
        }}
      >
        <IconButton
          onClick={() => handleTogglePanel(!isPanelOpen)}
          size='small'
          sx={{
            'width': 24,
            'height': 24,
            'backgroundColor': 'white',
            'border': 1,
            'borderColor': grey[400],

            '&:hover': {
              'backgroundColor': cyan[300],

              '& svg': {
                color: 'white',
              },
            },
          }}
        >
          {isPanelOpen ? (
            <KeyboardArrowLeftIcon
              sx={{
                width: 16,
                height: 16,
                color: grey[400],
              }}
            />
          ) : (
            <KeyboardArrowRightIcon
              sx={{
                width: 16,
                height: 16,
                color: grey[400],
              }}
            />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default NavigationPanel;
